/*
Treatment-arm Skio plan picker for the BAR-22 subscription test.

Subclasses the base Skio plan picker component and overrides render() for
the teal subscribe-first layout used by IG variants B and C. Shipped as a
separate file so the control arm's component stays untouched; removable
in one PR when the experiment ends.

Base-class access: we don't `import { SkioPlanPickerComponent }` from the
base file, because Shopify's `asset_url` filter appends a cache-busting
`?v=…` query to the base's <script> tag, while a relative import here
resolves to the un-busted URL. The browser treats them as two different
modules, both try to `customElements.define('skio-plan-picker', …)`, and
the second throws — halting this file before it can register the variant
element. Instead we wait for the base's registration via
`customElements.whenDefined` and read the constructor from the registry.

Form-input gating: each picker finds its hidden form input via
`this.nextElementSibling`, so three pickers = three sibling input pairs.
To prevent hidden-arm pickers from writing competing selling_plan values,
we gate `updateForm()` on IG's `ig:ready` event. Until IG confirms the
active arm, no instance writes. After `ig:ready`, only the instance
whose IG wrapper is visible writes.
*/

import { html, css } from 'https://cdn.jsdelivr.net/gh/lit/dist@2/core/lit-core.min.js';

await customElements.whenDefined('skio-plan-picker');
const SkioPlanPickerComponent = customElements.get('skio-plan-picker');

const BENEFITS_BY_VARIANT = {
  b: ['15% off every month', 'FREE shipping always', 'Skip, pause, or cancel anytime'],
  c: ['20% off first order, 15% off thereafter', 'FREE shipping always', 'Skip, pause, or cancel anytime'],
};

const SAVE_LABEL_BY_VARIANT = {
  b: '15%',
  c: '20%',
};

const variantStyles = css`
  :host {
    --skio-gray:        #595955;
    --skio-gray-dark:   #949596;
    --skio-teal:        #B0D2D4;
    --skio-teal-light:  #E4F2F1;
    --skio-teal-dark:   #00555A;
  }

  .skio-plan-picker-variant {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-width: 520px;
    margin: 0;
    margin-bottom: 24px;
    padding: 0;
    border: 0;
    font-family: 'brandon-grotesque', sans-serif;
    color: var(--skio-gray);
  }
  


  /* Shared card styles */
  .skio-sub-card,
  .skio-onetime {
    overflow: hidden;
    border: 1px solid var(--skio-gray-dark);
    border-radius: 8px;
    background-color: white;
    cursor: pointer;
  }

  .skio-sub-card--selected,
  .skio-onetime--selected {
    border-color: var(--skio-teal-dark);
    box-shadow: inset 0 0 0 1px var(--skio-teal-dark);
    background-color: var(--skio-teal-light);
  }
  
  .skio-sub-card__topline,
  .skio-onetime {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 8px;
    line-height: 1;
    font-size: 18px;
    font-weight: 450;
  }

  .skio-sub-card__left,
  .skio-sub-card__right,
  .skio-onetime__left,
  .skio-onetime__right {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    column-gap: 8px;
    row-gap: 4px;
  }

  .skio-onetime__right {
    justify-content: end;
  }

  .skio-visually-hidden {
    overflow: hidden;
    position: absolute;
    clip: rect(0,0,0,0);
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    white-space: nowrap;
    border: 0;
  }

  .skio-radio-dot {
    display: inline-flex;
    width: 14px;
    height: 14px;
    border: 1px solid var(--skio-teal-dark);
    border-radius: 9999px;
  }

  .skio-radio-dot--filled {
    background-color: var(--skio-teal-dark);
  }

  .skio-sub-card__was,
  .skio-onetime__was {
    font-size: 14px;
    font-weight: 390;
    // text-decoration-line: line-through;
    color: var(--skio-gray);
  }



  /* Subscription card styles */
  .skio-sub-card--selected .skio-sub-card__header {
    border-color: var(--skio-teal-dark);
    background-color: var(--skio-teal-dark);
    color: white;
  }

  .skio-sub-card--selected .skio-sub-card__frequency {
    display: block;
  }

  .skio-sub-card__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 12px;
    border-bottom: 1px solid var(--skio-gray-dark);
    line-height: 1;
    font-size: 12px;
  }

  .skio-sub-card__best-value,
  .skio-sub-card__save {
    letter-spacing: 1px;
  }

  .skio-sub-card__save {
    padding: 4px 8px;
    background-color: var(--skio-teal);
    font-size: 12px;
    color: var(--skio-teal-dark);
  }

  .skio-sub-card__benefits {
    display: flex;
    flex-direction: column;
    row-gap: 8px;
    margin: 0;
    margin-bottom: 12px;
    padding-inline-start: 40px;
    list-style: none;
    line-height: 1;
  }

  .skio-sub-card__benefits li {
    display: flex;
    align-items: center;
    column-gap: 8px;
  }

  .skio-sub-card__frequency {
    display: none;
    margin-block: 12px;
    padding-inline-start: 40px;
  }

  .skio-sub-card__frequency select {
    width: calc(100% - 12px);
    padding: 4px 10px;
    border: 1px solid var(--skio-teal-dark);
    border-radius: 4px;
    font: inherit;
    color: inherit;

    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'%3E%3Cpath d='M0.666656 4.66667L7.99999 11.3333L15.3333 4.66667' stroke-linecap='round' stroke-linejoin='round' stroke='%234C4646'/%3E%3C/svg%3E");
    background-position: right 10px center;
    background-repeat: no-repeat;
    cursor: pointer;
  }



  /* Desktop adjustments */
  @media screen and (min-width: 1024px) {
    .skio-sub-card__topline,
    .skio-onetime {
      padding-inline: 12px;
      font-size: 20px;
    }
    
    .skio-radio-dot {
      width: 18px;
      height: 18px;
    }
  }
`;

class SkioPlanPickerVariant extends SkioPlanPickerComponent {
  static properties = {
    ...SkioPlanPickerComponent.properties,
    variant: { type: String },
    sellingPlanGroupId: { type: String, attribute: 'selling-plan-group-id' },
  };

  static styles = [SkioPlanPickerComponent.styles, variantStyles];

  constructor() {
    super();
    this.variant = 'b';
    this.sellingPlanGroupId = '';
    this.startSubscription = true;
    this._igReady = false;
    this._igInactiveArm = false;
  }

  connectedCallback() {
    super.connectedCallback();

    const resolveArm = () => {
      if (this._igReady) return;
      this._igReady = true;
      const wrapper = this.closest('[class*="ig-subscription-test"]');
      this._igInactiveArm = !!wrapper && getComputedStyle(wrapper).display === 'none';
      if (!this._igInactiveArm) this.updateForm();
    };

    document.addEventListener('ig:ready', resolveArm, { once: true });
    // Fallback for pages without IG (preview without an experiment, broken script, etc.)
    setTimeout(resolveArm, 500);
  }

  updateForm() {
    if (!this._igReady || this._igInactiveArm) return;
    super.updateForm();

    // C only: parent writes `properties[Discount]` which the cart template
    // then renders as a stray "Discount: 20%" line next to the %-off copy
    // already carried in the plan name. Three arms each have their own hidden
    // input with the same `name` on the same form, so scope to C's own wrapper
    // instead of the form-wide querySelector (which returns Control's input).
    if (this.variant === 'c') {
      const discount_input = this.parentElement?.querySelector('[data-skio-discount-input]');
      if (discount_input) {
        discount_input.disabled = true;
        discount_input.value = '';
      }
    }
  }

  // Pick the Skio group this variant should bind to.
  // When `selling-plan-group-id` is passed, prefer that; otherwise fall back to the first available.
  _activeGroup() {
    if (!this.availableSellingPlanGroups?.length) return null;
    if (this.sellingPlanGroupId) {
      const match = this.availableSellingPlanGroups.find(g => g.id === this.sellingPlanGroupId);
      if (match) return match;
    }
    return this.availableSellingPlanGroups[0];
  }

  _benefits() { return BENEFITS_BY_VARIANT[this.variant] || BENEFITS_BY_VARIANT.b; }
  _saveLabel() { return SAVE_LABEL_BY_VARIANT[this.variant] || SAVE_LABEL_BY_VARIANT.b; }

  render() {
    if (!this.product || !this.selectedVariant || !this.product?.available) return;

    const group = this.skioSellingPlanGroups.length > 0 ? this._activeGroup() : null;
    const onetimeSelected = this.selectedSellingPlanGroup == null;

    const checkIcon = html`
      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path d="M1 7L5 11L11 1" stroke-linecap="round" stroke-linejoin="round" stroke="#4C4646"/>
      </svg>
    `;

    return html`
      <fieldset class="skio-plan-picker-variant" skio-plan-picker="${this.key}">
        ${group ? this._renderSubscribeCard(group, checkIcon) : html``}

        <!-- One-time row -->
        <div class="skio-onetime ${onetimeSelected ? 'skio-onetime--selected' : ''}"
             skio-group-container skio-group-container--onetime
             @click=${() => { this.trackSelection(null); this.selectSellingPlanGroup(null); }}>
          <div class="skio-onetime__left">
            <input class="skio-visually-hidden" type="radio"
                   id="skio-onetime-${this.key}" name="skio-group-${this.key}"
                   value="" skio-one-time
                   ?checked=${onetimeSelected}>
            <span class="skio-radio-dot ${onetimeSelected ? 'skio-radio-dot--filled' : ''}"></span>
            <span class="skio-onetime__title">One Time Purchase</span>
            ${!group ? html`<span class="skio-sub-card__save">BEST VALUE</span>` : html``}
          </div>
          <div class="skio-onetime__right">
            ${this.offer().savings > 0 ? html`<s class="skio-onetime__was">${this.money(this.offer().compareAtPrice)}</s>` : html``}
            <span class="skio-onetime__now">${this.money(this.offer().price)}</span>
          </div>
        </div>
      </fieldset>
    `;
  }

  _renderSubscribeCard(group, checkIcon) {
    const plan = group.selected_selling_plan || group.selling_plans?.[0];
    const subSelected = this.selectedSellingPlanGroup === group;
    const offer = this.offer(plan);
    const discount = this.discount(plan);

    return html`
      <div class="skio-sub-card ${subSelected ? 'skio-sub-card--selected' : ''}"
           skio-group-container
           @click=${() => { this.trackSelection(group); this.selectSellingPlanGroup(group); }}>
        <div class="skio-sub-card__header">
          <span class="skio-sub-card__best-value">BEST VALUE</span>
          ${this.shouldShowPerDayPrice() ? html`
            <span class="skio-sub-card__per-day">Only ${this.perDayPrice(offer.perDeliveryPrice)} a day</span>
          ` : html``}
        </div>
        <div class="skio-sub-card__body">
          <div class="skio-sub-card__topline">
            <div class="skio-sub-card__left">
              <input class="skio-visually-hidden" type="radio"
                     id="skio-sub-${this.key}" name="skio-group-${this.key}"
                     value="${group.id}" skio-selling-plan-group="${group.id}"
                     ?checked=${subSelected}>
              <span class="skio-radio-dot ${subSelected ? 'skio-radio-dot--filled' : ''}"></span>
              <span class="skio-sub-card__title">Subscribe</span>
              <span class="skio-sub-card__save">SAVE ${this._saveLabel()}</span>
            </div>
            <div class="skio-sub-card__right">
              ${discount.amount > 0 ? html`<s class="skio-sub-card__was">${this.money(offer.compareAtPrice)}</s>` : html``}
              <span class="skio-sub-card__now">${this.price(plan)}</span>
            </div>
          </div>

          <ul class="skio-sub-card__benefits">
            ${this._benefits().map(b => html`<li>${checkIcon}<span>${b}</span></li>`)}
          </ul>

          <div class="skio-sub-card__frequency">
            <select skio-selling-plans="${group.id}"
                    @change=${(e) => this.selectSellingPlan(e.target, group)}
                    @click=${(e) => e.stopPropagation()}>
              ${group.selling_plans.map((p) => html`
                <option value="${p.id}" ?selected=${group.selected_selling_plan === p}>
                  Delivered ${p.name.split(' - ')[0]}
                </option>
              `)}
            </select>
          </div>
        </div>
      </div>
    `;
  }
}

customElements.define('skio-plan-picker-variant', SkioPlanPickerVariant);
